import { http } from "@/lib/request";
import type { PageParams, PageResult } from "@/types/api";

export interface CollectItem {
  id: number;
  relation_id?: number;
  name?: string;
  work_photo?: string;
  image?: string;
  shop_id?: number;
  work_status?: number;
  server_status?: number;
  order_num?: number;
  good_comment?: string;
  comment_num?: number;
  collect_num?: number;
  is_collect?: number;
  first_appoint?: string;
  distance_desc?: string;
}

export interface UserProfileInfo {
  id?: number;
  user_id?: number;
  nickname?: string;
  avatar?: string;
  mobile?: string;
  sex?: number;
  create_time?: string;
  createTime?: string;
}

export interface CouponItem {
  id: number;
  coupon_id: number;
  money: string;
  condition: string;
  tips: string;
  status: number;
  status_desc: string;
  use_start_time_text?: string;
  use_end_time_text?: string;
}

export interface UserCenterResult {
  id: number;
  user_id: number;
  nickname?: string;
  avatar?: string;
  mobile?: string;
  user_money?: number;
  user_earnings?: number;
  collect_coach_num?: number;
  collect_goods_num?: number;
  collect_shop_num?: number;
  goods_comment_num?: number;
  wait_comment_num?: number;
  waitpay_order_num?: number;
  appoint_order_num?: number;
  server_order_num?: number;
  complete_order_num?: number;
  coupon_num?: number;
}

export interface CustomerAddressItem {
  id: number;
  contact?: string;
  gender?: number;
  gender_desc?: string;
  mobile?: string;
  province_id?: number | null;
  city_id?: number | null;
  district_id?: number | null;
  province?: string;
  city?: string;
  district?: string;
  address?: string;
  house_number?: string;
  longitude?: number | string | null;
  latitude?: number | string | null;
  is_default?: number;
  usable?: number;
  distance_disable?: number;
  server_disable?: number;
}

export interface CustomerAddressListResult {
  usable: CustomerAddressItem[];
  distance_disable: CustomerAddressItem[];
  server_disable: CustomerAddressItem[];
}

export interface CustomerAddressPayload {
  id?: number;
  contact: string;
  gender: number;
  mobile: string;
  province_id?: number | null;
  city_id?: number | null;
  district_id?: number | null;
  province_name?: string;
  city_name?: string;
  district_name?: string;
  address: string;
  house_number: string;
  is_default: number;
  longitude?: number | null;
  latitude?: number | null;
}

export interface CustomerFeedbackPayload {
  feedbackType: string;
  content: string;
  contactEmail?: string;
  ratingOverall?: number;
  ratingTherapist?: number;
  ratingApp?: number;
}

export interface KefuConfigResult {
  way?: string;
  name?: string;
  remarks?: string;
  phone?: string;
  service_phone?: string;
  business_time?: string;
  qr_code?: string;
  enterprise_id?: string;
  kefu_link?: string;
}

export interface CustomerAdItem {
  id: number;
  title?: string;
  subtitle?: string;
  tag_text?: string;
  button_text?: string;
  image_url?: string;
  background_color?: string;
  accent_color?: string;
  link_type?: "NONE" | "PAGE" | "URL" | "GOODS" | string;
  link_value?: string;
  sort?: number;
}

export interface CustomerAdPositionResult {
  code: string;
  name?: string;
  items: CustomerAdItem[];
}

export interface WalletSummaryResult {
  user_money?: number | string;
  user_earnings?: number | string;
  total_money?: number | string;
  recharge_open?: number;
  card_amounts?: Array<number | string>;
}

export interface WalletAccountLogItem {
  id: number;
  change_type_desc?: string;
  change_amount?: number | string;
  action?: number;
  create_time?: string;
  remark?: string;
}

export interface WalletAccountLogResult {
  lists: WalletAccountLogItem[];
  count: number;
  page_no: number;
  page_size: number;
}

export interface WithdrawTypeItem {
  name?: string;
  value?: number;
}

export interface WithdrawConfigResult {
  user_earnings?: number | string;
  min_withdraw?: number | string;
  max_withdraw?: number | string;
  percentage?: number | string;
  type: WithdrawTypeItem[];
}

export interface RechargeApplyPayload {
  money: number | string;
  pay_way?: number;
}

export interface RechargeApplyResult {
  order_id: number;
  from?: string;
  money?: number | string;
}

export interface WithdrawApplyPayload {
  type: number;
  money: number | string;
  account?: string;
  real_name?: string;
  bank?: string;
  subbank?: string;
  money_qr_code?: string;
}

export interface WithdrawApplyResult {
  id: number;
  status?: number;
  money?: number | string;
}

export interface WithdrawLogItem {
  id: number;
  sn?: string;
  status?: number;
  status_desc?: string;
  money?: number | string;
  create_time?: string;
  wechat_confirm_required?: boolean;
}

export interface WechatWithdrawPrepareResult {
  id: number;
  out_bill_no?: string;
  transfer_bill_no?: string;
  state?: string;
  package_info?: string;
  app_id?: string;
  mch_id?: string;
  need_user_confirm?: boolean;
}

export interface FinancePayResult {
  id: number;
  sn?: string;
  pay_way?: number;
  pay_status?: number;
  pay_way_desc?: string;
  order_amount?: number | string;
  total_amount?: number | string;
}

export interface FinanceMockPayNotifyResult {
  order_id: number;
  result: string;
  message?: string;
  pay_status: number;
}

export interface FinancePrepayResult {
  order_id?: number;
  pay_way?: number;
  config?: Record<string, unknown>;
}

export const profileApi = {
  getUserCenter() {
    return http.get<UserCenterResult>("/user/center");
  },

  getUserInfo() {
    return http.get<UserProfileInfo>("/user/info");
  },

  getCollectList(params: PageParams & { type: number }) {
    return http.get<PageResult<CollectItem>>("/user/collectLists", { params });
  },

  getCouponList(params: PageParams & { type?: number }) {
    return http.get<PageResult<CouponItem>>("/coupon/myCoupon", { params });
  },

  getWallet() {
    return http.get<WalletSummaryResult>("/user/wallet");
  },

  getWalletAccountLogs(params?: PageParams) {
    return http.get<WalletAccountLogResult>("/account_log/lists", { params });
  },

  applyRecharge(payload: RechargeApplyPayload) {
    return http.post<RechargeApplyResult>("/recharge/recharge", payload);
  },

  prepayFinance(payload: { from: string; order_id: number; pay_way: number }) {
    return http.post<FinancePrepayResult>("/pay/prepay", payload);
  },

  mockFinancePayNotify(orderId: number, result: "success" | "fail" | "cancel", payWay: number, from: string) {
    return http.post<FinanceMockPayNotifyResult>("/pay/mock/notify", {
      order_id: orderId,
      result,
      pay_way: payWay,
      from,
    });
  },

  getFinancePayResult(orderId: number, from: string) {
    return http.get<FinancePayResult>("/pay/getPayResult", {
      params: { order_id: orderId, from },
    });
  },

  getWithdrawConfig() {
    return http.get<WithdrawConfigResult>("/withdraw/getConfig");
  },

  applyWithdraw(payload: WithdrawApplyPayload) {
    return http.post<WithdrawApplyResult>("/withdraw/apply", payload);
  },

  getWithdrawLogs(params?: PageParams) {
    return http.get<PageResult<WithdrawLogItem>>("/withdraw/lists", { params });
  },

  prepareWechatWithdraw(id: number) {
    return http.post<WechatWithdrawPrepareResult>("/withdraw/wechat/prepare", { id });
  },

  getAddressList(params?: { goods_id?: number; coach_id?: number }) {
    return http.get<CustomerAddressListResult>("/user_address/lists", { params });
  },

  getAddressDetail(id: number) {
    return http.get<CustomerAddressItem>("/user_address/detail", { params: { id } });
  },

  addAddress(payload: CustomerAddressPayload) {
    return http.post<number>("/user_address/add", payload);
  },

  editAddress(payload: CustomerAddressPayload) {
    return http.post<void>("/user_address/edit", payload);
  },

  deleteAddress(id: number) {
    return http.post<void>("/user_address/del", { id });
  },

  setDefaultAddress(id: number) {
    return http.post<void>("/user_address/setDefault", { id });
  },

  submitFeedback(payload: CustomerFeedbackPayload) {
    return http.post<void>("/feedback/submit", payload);
  },

  getKefuConfig(cityId?: number | string | null) {
    const resolvedCityId = Number(cityId || 0);
    const payload = Number.isFinite(resolvedCityId) && resolvedCityId > 0
      ? { city_id: resolvedCityId }
      : {};
    return http.post<KefuConfigResult>("/config/getKefuConfig", payload, {
      withAuth: false,
    });
  },

  getAdPosition(code: string) {
    return http.get<CustomerAdPositionResult>("/ad/position", {
      params: { code },
      withAuth: false,
    });
  },

  updateUserInfo(payload: {
    nickname?: string;
    avatar?: string;
    mobile?: string;
    sex?: number;
  }) {
    return http.post<void>("/user/setInfo", payload);
  },

  uploadAvatar(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return http.post<string>("/user/avatar", formData);
  },
};
